<template>
  <main class="min-h-screen bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-200">
    <div class="mx-auto flex w-full max-w-3xl px-5 pt-5">
      <a
        :href="workspacePath"
        class="text-sm font-semibold text-slate-600 outline-none hover:text-slate-950 focus:ring-2 focus:ring-indigo-500 dark:text-slate-400 dark:hover:text-slate-100"
        @click.prevent="requestReturnToTasks"
      >
        ← Tasks
      </a>
    </div>

    <div v-if="error" class="mx-auto mt-5 w-full max-w-3xl px-5">
      <div class="bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
        {{ error }}
      </div>
    </div>

    <TaskDetail
      ref="taskDetailRef"
      :store="store"
      :task-id="currentTaskId"
      :can-edit="canEdit"
      :lock-holder="lockHolder"
      @deleted="returnToTasks"
      @moved="returnToMovedWorkspace"
    />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import TaskDetail from './TaskDetail.vue'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  projects: {
    type: Array,
    default: () => [],
  },
  initialTaskId: {
    type: [String, Number],
    required: true,
  },
  workspaceSlug: {
    type: String,
    required: true,
  },
  workspaceName: {
    type: String,
    default: '',
  },
  currentUserLabel: {
    type: String,
    default: '',
  },
  isWorkspaceOwner: {
    type: Boolean,
    default: false,
  },
})

const taskDetailRef = ref(null)
const error = ref('')
const lockedInfo = ref(null)
let pageExitReleaseSent = false

const currentTaskId = computed(() => normalizeId(props.initialTaskId))
const workspacePath = computed(() => `/workspaces/${encodeURIComponent(props.workspaceSlug)}`)
const task = computed(() => {
  const detail = props.store.activeTaskDetail.value
  return normalizeId(detail?.id) === currentTaskId.value ? detail : null
})
const canEdit = computed(() => Boolean(task.value && props.store.heldToken(task.value.id)))
const lockHolder = computed(() => {
  const label = lockedInfo.value?.locked_by_label || task.value?.locked_by_label
  const via = lockedInfo.value?.locked_via || task.value?.locked_via
  if (!label && !via) return null
  return `${label || 'another user'}${via ? ` via ${via}` : ''}`
})

watch(
  () => props.projects,
  (projects) => props.store.setProjects?.(projects),
  { immediate: true }
)

onMounted(async () => {
  props.store.openTask(currentTaskId.value)
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', releaseLockOnPageExit)

  await acquireLockForTask(currentTaskId.value)
  await loadDetail(currentTaskId.value)
})

onBeforeUnmount(() => {
  releaseCurrentLock().catch(() => {})
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('pagehide', releaseLockOnPageExit)
})

async function acquireLockForTask(id) {
  const taskId = normalizeId(id)
  if (!taskId) return false

  if (props.store.heldToken(taskId)) {
    clearLockState()
    return true
  }

  try {
    await props.store.lockTask(taskId, {
      locked_by_label: props.currentUserLabel,
      locked_via: 'web',
    })
    clearLockState()
    return true
  } catch (requestError) {
    applyLockError(requestError)
    return false
  }
}

async function loadDetail(id) {
  error.value = ''

  try {
    await props.store.loadTaskDetail(id)
  } catch (requestError) {
    error.value = requestError?.message || 'Task could not be loaded'
  }
}

async function releaseCurrentLock() {
  const taskId = currentTaskId.value
  if (!taskId) return

  await taskDetailRef.value?.flushPendingSave?.(taskId)
  await props.store.unlockTask(taskId)
}

function releaseLockOnPageExit() {
  const taskId = currentTaskId.value
  const token = taskId ? props.store.heldToken(taskId) : null
  if (!taskId || !token || pageExitReleaseSent) return

  pageExitReleaseSent = true
  fetch(props.store.lockEndpointUrl(taskId), {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
      'X-Lock-Token': token,
    },
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => {})
}

function handleBeforeUnload(event) {
  if (!taskDetailRef.value?.hasUnsavedChanges?.()) return

  event.preventDefault()
  event.returnValue = ''
}

async function requestReturnToTasks() {
  try {
    await releaseCurrentLock()
  } catch (requestError) {
    error.value = saveFailureMessage(requestError)
    return
  }

  returnToTasks()
}

function returnToTasks() {
  window.location.href = workspacePath.value
}

function returnToMovedWorkspace(movedTask) {
  const workspaceSlug = movedTask?.workspace?.slug
  window.location.href = workspaceSlug ? `/workspaces/${encodeURIComponent(workspaceSlug)}` : workspacePath.value
}

function saveFailureMessage(requestError) {
  return requestError?.message || 'Task could not be saved'
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ''
}

function clearLockState() {
  lockedInfo.value = null
}

function applyLockError(requestError) {
  const info = lockInfoFromError(requestError)
  if (info.locked_by_label || info.locked_via || info.locked_at) {
    lockedInfo.value = info
  }
}

function lockInfoFromError(requestError) {
  const data = requestError?.data || requestError || {}

  return {
    locked_by_label: data.locked_by_label,
    locked_via: data.locked_via,
    locked_at: data.locked_at,
  }
}

function normalizeId(id) {
  return id === null || id === undefined ? null : String(id)
}
</script>
